import { useEffect, useState } from "react";
import { Brain, CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function DailyQuiz() {
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null); // index of picked option for current Q
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/quiz/daily").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return (
      <section
        className="bg-white border border-slate-200 rounded-xl p-5 md:p-6"
        data-testid="daily-quiz-loading"
      >
        <div className="h-5 w-32 bg-slate-100 rounded mb-3 animate-pulse" />
        <div className="h-3 w-full bg-slate-100 rounded mb-2 animate-pulse" />
        <div className="h-3 w-2/3 bg-slate-100 rounded animate-pulse" />
      </section>
    );
  }

  const questions = data.questions || [];
  const total = questions.length;
  const q = questions[idx];

  const choose = (i) => {
    if (picked != null) return;
    setPicked(i);
    if (i === q.answer) setScore((s) => s + 1);
  };

  const next = () => {
    if (idx + 1 < total) {
      setIdx(idx + 1);
      setPicked(null);
    } else {
      setDone(true);
    }
  };

  const restart = () => {
    setIdx(0);
    setPicked(null);
    setScore(0);
    setDone(false);
  };

  return (
    <section
      className="bg-white border border-slate-200 rounded-xl p-5 md:p-6"
      aria-labelledby="daily-quiz-h2"
      data-testid="daily-quiz"
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <span className="inline-grid place-items-center w-8 h-8 rounded-md bg-orange-100 text-orange-700">
            <Brain className="w-4 h-4" aria-hidden="true" />
          </span>
          <h2
            id="daily-quiz-h2"
            className="text-lg md:text-xl font-extrabold text-slate-900"
            style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}
          >
            Daily 5-Q Booster Quiz
          </h2>
        </div>
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500" data-testid="daily-quiz-date">
          {data.date}
        </span>
      </div>
      <p className="text-xs text-slate-600 mb-4">
        GK, Current Affairs & Aptitude — refreshed every day at 00:00 IST.
      </p>

      {!done ? (
        <div>
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-orange-600">
              Q {idx + 1} / {total}
            </span>
            <span className="text-[11px] font-semibold text-slate-500">
              Topic: {q.topic}
            </span>
          </div>
          <p className="text-sm md:text-base font-semibold text-slate-900 mb-3 leading-snug" data-testid="quiz-question">
            {q.q}
          </p>
          <ul className="space-y-2">
            {q.options.map((opt, i) => {
              const isPicked = picked === i;
              const isCorrect = q.answer === i;
              const reveal = picked != null;
              let cls = "border-slate-200 bg-white hover:bg-slate-50";
              if (reveal && isCorrect) cls = "border-green-300 bg-green-50 text-green-900";
              else if (reveal && isPicked && !isCorrect) cls = "border-red-300 bg-red-50 text-red-900";
              else if (reveal) cls = "border-slate-200 bg-white opacity-70";
              return (
                <li key={i}>
                  <button
                    type="button"
                    onClick={() => choose(i)}
                    disabled={picked != null}
                    data-testid={`quiz-option-${i}`}
                    className={`w-full text-left text-sm font-medium min-h-11 px-3 py-2 rounded-md border transition-colors flex items-center justify-between gap-2 ${cls}`}
                  >
                    <span>{opt}</span>
                    {reveal && isCorrect && <CheckCircle2 className="w-4 h-4 text-green-700 flex-shrink-0" />}
                    {reveal && isPicked && !isCorrect && <XCircle className="w-4 h-4 text-red-700 flex-shrink-0" />}
                  </button>
                </li>
              );
            })}
          </ul>
          {picked != null && (
            <div className="mt-3 p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900 leading-relaxed">
              <strong className="text-amber-700">Why:</strong> {q.explain}
            </div>
          )}
          {picked != null && (
            <Button
              onClick={next}
              data-testid="quiz-next"
              className="mt-3 w-full bg-orange-600 hover:bg-orange-700 text-white min-h-11"
            >
              {idx + 1 < total ? "Next question →" : "See my score"}
            </Button>
          )}
        </div>
      ) : (
        <div className="text-center py-2" data-testid="quiz-done">
          <div className="text-5xl font-extrabold text-orange-600" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
            {score} / {total}
          </div>
          <p className="text-sm text-slate-700 mt-2">
            {score === total
              ? "Perfect! You're ready for the exam hall."
              : score >= 3
              ? "Solid — keep at it daily."
              : "Tomorrow&apos;s quiz drops fresh at midnight."}
          </p>
          <Button
            variant="outline"
            onClick={restart}
            data-testid="quiz-restart"
            className="mt-4 min-h-11"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Retry today&apos;s set
          </Button>
        </div>
      )}
    </section>
  );
}
